/**
 * ShellSkeleton — the app's frame (appbar + left rail + content blocks)
 * rendered as shimmering placeholders. Shown instead of a bare spinner
 * during boot (config load), auth resolution, and lazy-route loads, so the
 * user sees the page's structure instantly instead of a spinner cascade.
 *
 * Pure CSS, zero data dependencies, safe to render before config/theme.
 */
import * as React from 'react';

const bar = (w: string | number, h = 12, r = 6): React.CSSProperties => ({
  width: w,
  height: h,
  borderRadius: r,
  background: 'linear-gradient(90deg, var(--surface-hover, #ECECEF) 25%, var(--surface-2, #F6F6F8) 45%, var(--surface-hover, #ECECEF) 65%)',
  backgroundSize: '400% 100%',
  animation: 'ajo-shimmer 1.4s ease infinite',
});

// Self-recovery: this skeleton renders in EVERY stuck path (boot/config
// load, auth resolution, lazy-route chunk loads). If one instance stays
// mounted 20s, something upstream hung. First two occurrences within 2
// minutes auto-reload the page (heals stale-bundle tabs and hung fetches);
// after that, show a manual Reload button instead of looping.
function useStuckRecovery() {
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[skeleton] stuck 20s on', window.location.pathname, {
        hasOktaTokens: !!localStorage.getItem('okta-token-storage'),
        logoutSentinel: !!sessionStorage.getItem('ajo:logout-in-progress'),
      });
      try {
        const now = Date.now();
        const raw = sessionStorage.getItem('ajo:skeleton-reloads');
        const recent = (raw ? (JSON.parse(raw) as number[]) : []).filter((ts) => now - ts < 120_000);
        if (recent.length < 2) {
          recent.push(now);
          sessionStorage.setItem('ajo:skeleton-reloads', JSON.stringify(recent));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage blocked — fall through to the button */ }
      setStuck(true);
    }, 20_000);
    return () => clearTimeout(t);
  }, []);
  return stuck;
}

export default function ShellSkeleton() {
  const stuck = useStuckRecovery();
  if (stuck) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #F7F7F8)', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #1A1A1A)', marginBottom: 8 }}>
            This is taking longer than it should
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary, #5A5A66)', marginBottom: 16, lineHeight: 1.5 }}>
            The app couldn’t finish loading. Reload to try again — if this keeps
            happening, check your network/VPN connection.
          </div>
          <button
            type="button"
            style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--primary, #1A1A1A)', color: 'var(--surface, #fff)', fontFamily: 'inherit' }}
            onClick={() => {
              try { sessionStorage.removeItem('ajo:skeleton-reloads'); } catch { /* best-effort */ }
              window.location.reload();
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      aria-hidden
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateRows: '56px 1fr',
        gridTemplateColumns: '264px 1fr',
        gridTemplateAreas: '"appbar appbar" "rail content"',
        background: 'var(--bg, #F7F7F8)',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <style>{'@keyframes ajo-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }'}</style>

      {/* appbar */}
      <div
        style={{
          gridArea: 'appbar',
          background: 'var(--surface, #FFFFFF)',
          borderBottom: '1px solid var(--border, #E5E5E8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={bar(28, 28, 6)} />
          <div style={bar(130, 14)} />
        </div>
        <div style={bar(28, 28, 14)} />
      </div>

      {/* left rail */}
      <div
        style={{
          gridArea: 'rail',
          background: 'var(--surface, #FFFFFF)',
          borderRight: '1px solid var(--border, #E5E5E8)',
          padding: '14px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={bar('60%')} />
        <div style={bar('75%')} />
        <div style={{ height: 8 }} />
        <div style={bar('45%', 10)} />
        <div style={bar('100%', 28, 7)} />
        <div style={{ height: 4 }} />
        <div style={bar('85%')} />
        <div style={{ ...bar('70%'), marginLeft: 16 }} />
        <div style={{ ...bar('65%'), marginLeft: 16 }} />
        <div style={bar('80%')} />
        <div style={bar('72%')} />
      </div>

      {/* content */}
      <div style={{ gridArea: 'content', padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={bar(220, 20)} />
        <div style={bar('55%')} />
        <div style={{ height: 8 }} />
        <div style={bar('90%')} />
        <div style={bar('84%')} />
        <div style={bar('88%')} />
        <div style={bar('60%')} />
      </div>
    </div>
  );
}
